import type { TimeInput, ZoneAbbrevStyle } from './types';
import { toDate } from './isomorphic/internal/coerce';
import { getDateTimeFormat } from './isomorphic/internal/formatter-cache';

/** Any `GMT±N` / `UTC±N` result is a raw offset, not a useful label — keep cascading. */
const isOffset = (s: string): boolean => /^(GMT|UTC)([+\-−]|$)/.test(s);

/**
 * Resolve the short timezone abbreviation (CST/CDT/PST/CET/…) for a given
 * instant in a zone. Machine-derived: cascades across locales because `en-US`
 * with `timeZoneName: 'short'` returns `GMT±N` for non-US zones like
 * `Europe/Berlin`. Falls back to the final IANA segment when every Intl attempt
 * returns a raw offset.
 *
 * Lifted from `apps/admin-web/.../DateTimePicker.tsx#formatAbbrev` (BUG-477) so
 * every app derives the same label from the same code.
 *
 * @param instant Reference instant — the abbreviation is date-dependent under DST.
 * @param zone IANA zone ID; empty string returned when undefined.
 * @param opts.style `specific` (default) keeps DST distinction (CST vs CDT);
 *                   `generic` collapses to the DST-agnostic form (CT).
 */
export function deriveZoneAbbrev(
    instant: TimeInput,
    zone: string | undefined,
    opts: { style?: ZoneAbbrevStyle } = {},
): string {
    if (!zone) return '';
    const at = toDate(instant);
    if (!at) return '';

    const tryIntl = (locale: string, type: 'short' | 'shortGeneric'): string | null => {
        try {
            const parts = getDateTimeFormat(locale, {
                timeZone: zone,
                timeZoneName: type,
            }).formatToParts(at);
            return parts.find((p) => p.type === 'timeZoneName')?.value ?? null;
        } catch {
            return null;
        }
    };

    // Generic style wants the DST-agnostic form first (ET/CT/PT/CET).
    if (opts.style === 'generic') {
        const generic = tryIntl('en-US', 'shortGeneric');
        if (generic && !isOffset(generic)) return generic;
    }

    // en-US yields CST/CDT/PST/MST for US zones (dominant callsite).
    const usShort = tryIntl('en-US', 'short');
    if (usShort && !isOffset(usShort)) return usShort;
    // en-GB resolves most European zones as CET/CEST/BST/… and still returns US abbrs.
    const gbShort = tryIntl('en-GB', 'short');
    if (gbShort && !isOffset(gbShort)) return gbShort;
    // Last resort that still returns a named (non-offset) form.
    const generic = tryIntl('en-US', 'shortGeneric');
    if (generic && !isOffset(generic)) return generic;

    return zone.split('/').pop()?.replace(/_/g, ' ') ?? '';
}
